import axios from "axios";

export const USER_STORAGE_KEY = "zubrus_user_data";
const TOKEN_BUFFER_MS = 15 * 1000;

const DEFAULT_HEADERS = {
  "X-Timezone-Id": "Europe/Vilnius",
  "Content-Type": "application/json",
  "X-Version": "v3",
};

export function getStoredUserBundle() {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredTokens() {
  return getStoredUserBundle()?.data?.tokens || null;
}

export function getStoredUserInfo() {
  return getStoredUserBundle()?.data?.userInfo || null;
}

export function hasPersistedSession() {
  const tokens = getStoredTokens();
  return Boolean(tokens?.refreshToken || tokens?.accessToken);
}

export function getHomePath(userInfo = getStoredUserInfo()) {
  const roles = userInfo?.userRoles || [];
  if (roles.length === 1 && roles[0] === "zubrus_admin") {
    return "/admin/userlist";
  }
  if (roles.includes("zubrus_parent")) {
    return "/mychildren";
  }
  return "/classes";
}

export function updateStoredTokens(newTokens) {
  try {
    const bundle = getStoredUserBundle();
    if (!bundle?.data) return;
    bundle.data.tokens = { ...(bundle.data.tokens || {}), ...newTokens };
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(bundle));
  } catch {
    // storage write failed
  }
}

function decodeJwtPayload(token) {
  try {
    const part = String(token).split(".")[1];
    if (!part) return null;
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return null;
  }
}

export function isAccessTokenExpired(accessToken, bufferMs = TOKEN_BUFFER_MS) {
  if (!accessToken) return true;
  const payload = decodeJwtPayload(accessToken);
  if (!payload?.exp) return false;
  return payload.exp * 1000 - bufferMs <= Date.now();
}

let refreshPromise = null;

export async function refreshSessionTokens() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) return null;

    const BaseURL = process.env.REACT_APP_BaseURL;
    const headers = { ...DEFAULT_HEADERS };
    if (tokens.accessToken) {
      headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    const endpoints = ["/auth/token/refresh", "/auth/refresh"];
    for (const path of endpoints) {
      try {
        const res = await axios.post(
          `${BaseURL}${path}`,
          { refreshToken: tokens.refreshToken },
          { headers }
        );
        const payload = res?.data?.data || res?.data;
        const newTokens = payload?.tokens || payload;
        if (newTokens?.accessToken) {
          updateStoredTokens(newTokens);
          return newTokens;
        }
      } catch {
        // try the next known refresh path
      }
    }
    return null;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function ensureFreshSession() {
  if (!hasPersistedSession()) return false;
  const tokens = getStoredTokens();
  if (!isAccessTokenExpired(tokens?.accessToken)) return true;
  const refreshed = await refreshSessionTokens();
  return Boolean(refreshed?.accessToken);
}
